import React, { useEffect, useRef, useState } from "react";
import { DownloadSimple, X, ArrowUpRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "ht_pwa_nudge_dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad/.test(ua) && !window.navigator.standalone) return "ios";
  if (/Mac/.test(ua) && !window.chrome && !/Firefox|FxiOS/.test(ua)) return "macos";
  return "chromium";
}

export const PWADashboardNudge = () => {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [canPrompt, setCanPrompt] = useState(!!window.__htDeferredPrompt);
  const promptRef = useRef(window.__htDeferredPrompt || null);
  const platform = detectPlatform();

  useEffect(() => {
    const onReady = () => { promptRef.current = window.__htDeferredPrompt; setCanPrompt(true); };
    const onInstalled = () => { promptRef.current = null; setCanPrompt(false); };
    window.addEventListener("ht-installable", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("ht-installable", onReady);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (dismissed || isStandalone()) return null;
  if (platform === "chromium" && !canPrompt) return null;

  const close = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const install = async () => {
    const p = promptRef.current;
    if (!p) return;
    p.prompt();
    try { await p.userChoice; } catch { /* ignore */ }
    window.__htDeferredPrompt = null;
    promptRef.current = null;
    setCanPrompt(false);
  };

  const isAndroid = /Android/.test(navigator.userAgent || "");
  const text = {
    chromium: isAndroid ? "Installa Housetag sul tuo dispositivo" : "Installa Housetag sul tuo desktop",
    ios: "Tocca il tasto Condividi e poi Aggiungi alla schermata Home",
    macos: "Dal menu Safari clicca su File → Aggiungi al Dock",
  }[platform];

  return (
    <>
      {platform === "chromium" && !isAndroid && (
        <div className="hidden md:flex fixed top-1 right-24 z-50 pointer-events-none items-start gap-1 text-purple-700 animate-bounce" data-testid="pwa-nudge-arrow">
          <span className="mt-5 text-xs font-semibold bg-white/90 rounded-full px-2 py-0.5 shadow">Installa da qui</span>
          <ArrowUpRight size={30} weight="bold" />
        </div>
      )}
      <div className="mb-6 w-full flex items-center gap-3 rounded-xl border border-purple-200 bg-purple-50/70 px-4 py-3" data-testid="pwa-dashboard-nudge">
        <div className="shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center">
          <DownloadSimple size={20} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-purple-900 text-sm" data-testid="pwa-nudge-text">{text}</div>
          <div className="text-xs text-purple-700/80">Apri Housetag come un'app, più veloce e con accesso diretto.</div>
        </div>
        {platform === "chromium" && (
          <Button size="sm" onClick={install} className="bg-purple-600 hover:bg-purple-700 shrink-0" data-testid="pwa-nudge-install-btn">
            Installa
          </Button>
        )}
        <button type="button" onClick={close} className="shrink-0 p-1.5 rounded-full text-purple-700 hover:bg-purple-100 transition-colors" aria-label="Chiudi" data-testid="pwa-nudge-close">
          <X size={16} />
        </button>
      </div>
    </>
  );
};
